import { useCallback } from 'react';
import { getLabelByStorageIdPrefix } from '../services/shelfIndexAPI';
import { usePrintOrderRun, type PrintRunProgress, type PrintRunResult } from './usePrintOrderRun';
import type { PrintOrderSource } from '@/shared/types/printOrder.types';
import type { DestinationPrinterId } from '@/shared/types/printer.types';

export interface ReprintByPrefixParams {
  readonly storageIdPrefix: string;
  readonly isBigLabel: boolean;
  readonly copies: number;
  readonly onProgress?: (progress: PrintRunProgress) => void;
  readonly printerId?: DestinationPrinterId;
}

export interface UseReprintByPrefix {
  readonly reprint: (params: ReprintByPrefixParams) => Promise<PrintRunResult>;
  readonly isRunning: boolean;
}

/**
 * Reprint an existing label from its 6-char storage-id prefix as a tracked print order.
 * The label's real storage id is reused, so no new unique ID is minted and no
 * `shelf_indexes` row is written — the reprint is history only.
 */
export function useReprintByPrefix(source: PrintOrderSource): UseReprintByPrefix {
  const { runPrintOrder, isRunning } = usePrintOrderRun();

  const reprint = useCallback(
    async ({ storageIdPrefix, isBigLabel, copies, onProgress, printerId }: ReprintByPrefixParams): Promise<PrintRunResult> => {
      const prefix = String(storageIdPrefix || '').trim().toLowerCase();
      const label = await getLabelByStorageIdPrefix(prefix);
      if (!label) {
        return {
          orderId: null,
          totalLabels: 0,
          printedLabels: 0,
          completed: false,
          message: 'No label found for this storage ID.',
          order: null,
        };
      }

      return runPrintOrder(
        {
          labelKind: isBigLabel ? 'reprint_big' : 'reprint_warehouse',
          labelType: label.type ?? null,
          source,
          copiesPerId: copies,
          units: [
            {
              sku: label.sku,
              shelfIndex: label.shelfIndex ?? null,
              baseQty: label.baseQty ?? null,
              storageId: label.storageId,
            },
          ],
        },
        onProgress ? { onProgress, printerId } : { printerId },
      );
    },
    [runPrintOrder, source],
  );

  return { reprint, isRunning };
}
